import * as yup from 'yup';
import dayjs from 'dayjs';

import { RentCoverageTypes, SecurityDepositTypes } from '@/constants';
import { MONEY_VALIDATION_MESSAGES, isWholeNumber } from '@/shared/validators';
import type { CoveragePricing } from '@/shared/types';
import {
  validateMinLeaseDuration,
  validateMaxLeaseDuration,
} from '@/shared/utils/leaseDurationMonths.util';

import { INVITE_LEASE_DETAILS_VALIDATION_MESSAGES, ScreeningResult } from './constants';

const coverageMoneyField = yup
  .number()
  .transform((o, v) => (v ? Number.parseFloat(v.replaceAll(',', '')) : undefined))
  .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONEY_FIELD_TYPE_ERROR)
  .min(0, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONEY_FIELD_MIN)
  .max(50000, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONEY_FIELD_MAX);

const normalizeEmail = (email: string): string => {
  if (!email) {
    return '';
  }
  return email.toLowerCase();
};

const applicantEmails = yup
  .array()
  .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APPLICANT_EMAIL_REQUIRED)
  .max(6, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APPLICANT_EMAIL_MAX_COUNT)
  .of(
    yup.object().shape({
      value: yup
        .string()
        .email(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APPLICANT_EMAIL_INVALID)
        .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.EMAIL_REQUIRED),
    }),
  )
  .test(
    'Unique',
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APPLICANT_EMAIL_UNIQUE,
    function (values, { createError }) {
      if (!values?.length) {
        return true;
      }
      const normalizedEmails = values.map(({ value }) => normalizeEmail(value || ''));
      const isError = new Set(normalizedEmails).size !== normalizedEmails.length;
      if (isError) {
        return createError({
          path: 'applicantEmails',
          message: INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APPLICANT_EMAIL_UNIQUE_IGNORING_CASE,
        });
      }
      return true;
    },
  );

const createAddressSchema = () =>
  yup
    .object()
    .shape({
      monolith_building_id: yup
        .number()
        .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.ADDRESS_REQUIRED_MESSAGE),
      source: yup.string().optional().nullable(),
    })
    .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.ADDRESS_NOT_FOUND)
    .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.ADDRESS_REQUIRED_MESSAGE);

const unit = yup
  .string()
  .max(100, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.APARTMENT_NUMBER_TOO_LONG)
  .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.UNIT_NUMBER_REQUIRED);

const leaseStartDate = yup
  .date()
  .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_START_REQUIRED)
  .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_START_INVALID)
  .min(
    dayjs(new Date().setHours(0, 0, 0, 0))
      .subtract(1, 'year')
      .add(1, 'day')
      .toDate(),
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.DATE_ONE_YEAR_PAST,
  )
  .max(
    dayjs(new Date().setHours(23, 59, 59, 999))
      .add(1, 'year')
      .subtract(1, 'day')
      .toDate(),
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.DATE_ONE_YEAR_FUTURE,
  );

const leaseEndBase = yup
  .date()
  .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_END_REQUIRED)
  .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_END_INVALID)
  .test(
    'min-start-date',
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_END_AFTER_START,
    function (this: yup.TestContext, leaseEndDate) {
      if (!leaseEndDate) {
        return true;
      }
      return dayjs(this.parent.leaseStartDate) < dayjs(leaseEndDate);
    },
  )
  .min(
    dayjs(new Date().setHours(0, 0, 0, 0))
      .subtract(1, 'year')
      .add(1, 'day')
      .toDate(),
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.DATE_ONE_YEAR_PAST,
  )
  .max(
    dayjs(new Date().setHours(23, 59, 59, 999))
      .add(11, 'year')
      .subtract(1, 'day')
      .toDate(),
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.DATE_ONE_YEAR_FUTURE,
  );

const createLeaseEndSchema = (useNewCoveragesOnLandlordInvite?: boolean) => {
  const MIN_LEASE_DURATION_MONTHS = 3;
  const MAX_LEASE_DURATION_MONTHS = 36;

  let schema = leaseEndBase.test(
    'min-3-months-duration',
    INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_DURATION_MIN_THREE_MONTHS,
    function (this: yup.TestContext, leaseEndDate) {
      if (!leaseEndDate || !this.parent.leaseStartDate) {
        return true;
      }
      return validateMinLeaseDuration(
        this.parent.leaseStartDate,
        leaseEndDate,
        MIN_LEASE_DURATION_MONTHS,
      );
    },
  );

  if (useNewCoveragesOnLandlordInvite) {
    schema = schema.test(
      'max-36-months-duration',
      INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.LEASE_DURATION_MAX_THIRTY_SIX_MONTHS,
      function (this: yup.TestContext, leaseEndDate) {
        if (!leaseEndDate || !this.parent.leaseStartDate) {
          return true;
        }
        return validateMaxLeaseDuration(
          this.parent.leaseStartDate,
          leaseEndDate,
          MAX_LEASE_DURATION_MONTHS,
        );
      },
    );
  }

  return schema;
};

export const inviteLeaseDetailsSchema = (
  selfEnrolledSDRLimit?: number,
  customCoverageDisabled?: boolean,
  useNewCoveragesOnLandlordInvite?: boolean,
) =>
  yup.object().shape({
    applicantEmails,
    address: createAddressSchema(),
    unit,
    leaseStartDate,
    leaseEndDate: createLeaseEndSchema(useNewCoveragesOnLandlordInvite),
    grossMonthlyRent: coverageMoneyField
      .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.TOTAL_MONTHLY_RENT_REQUIRED)
      .test(
        'must-be-whole-number',
        MONEY_VALIDATION_MESSAGES.MUST_BE_WHOLE_NUMBER,
        function (this: yup.TestContext) {
          return isWholeNumber(this.originalValue);
        },
      ),
    prepaidRent: coverageMoneyField,
    freeRent: coverageMoneyField,
    screeningStatus: useNewCoveragesOnLandlordInvite
      ? yup
          .mixed<ScreeningResult | ''>()
          .oneOf(
            [
              '',
              ScreeningResult.Approved,
              ScreeningResult.ApprovedWithConditions,
              ScreeningResult.Denied,
            ],
            INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.INVALID_SCREENING_RESULT,
          )
          .optional()
          .notRequired()
          .default('')
      : yup.mixed<ScreeningResult | ''>().when('customCoverage', {
          is: (val: boolean) => val === false || val === undefined,
          then: (schema) =>
            schema
              .oneOf(
                [
                  ScreeningResult.Approved,
                  ScreeningResult.ApprovedWithConditions,
                  ScreeningResult.Denied,
                ],
                INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.INVALID_SCREENING_RESULT,
              )
              .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.SCREENING_RESULT_REQUIRED),
          otherwise: (schema) => schema.optional().notRequired(),
        }),
    leaseGuaranteeMonthsOverride: useNewCoveragesOnLandlordInvite
      ? yup
          .number()
          .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_REQUIRED)
          .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_REQUIRED)
          .min(0, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_MIN)
          .max(100, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_MAX)
      : yup.number().when(['customCoverage', 'rentCoverage'], {
          is: (customCoverage: boolean, rentCoverage: RentCoverageTypes) =>
            customCoverage === true || rentCoverage === RentCoverageTypes.Customize,
          then: (schema) =>
            schema
              .typeError(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_REQUIRED)
              .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_REQUIRED)
              .min(0, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_MIN)
              .max(100, INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.MONTHS_OF_COVERAGE_MAX),
          otherwise: (schema) => schema.optional().notRequired().default(undefined),
        }),

    securityDepositReplacementAmountOverride: yup
      .number()
      .when(
        ['customCoverage', 'securityDepositCoverage', 'includeDepositCoverage', 'grossMonthlyRent'],
        (
          [customCoverage, securityDepositCoverage, includeDepositCoverage, grossMonthlyRent],
          schema,
        ) => {
          if (useNewCoveragesOnLandlordInvite) {
            return schema.default(includeDepositCoverage ? grossMonthlyRent : 0);
          }
          if (
            customCoverage === true ||
            securityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove
          ) {
            if (selfEnrolledSDRLimit) {
              return coverageMoneyField
                .required(
                  INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.SECURITY_DEPOSIT_REPLACEMENT_REQUIRED,
                )
                .max(
                  grossMonthlyRent * selfEnrolledSDRLimit,
                  INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.amountCannotExceedRentMonths(
                    selfEnrolledSDRLimit,
                  ),
                );
            }
            return coverageMoneyField.required(
              INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.SECURITY_DEPOSIT_REPLACEMENT_REQUIRED,
            );
          }
          return schema.optional().notRequired().default(undefined);
        },
      ),

    rentCoverage: customCoverageDisabled
      ? yup
          .string()
          .optional()
          .oneOf([RentCoverageTypes.CompanyDefault, RentCoverageTypes.Customize])
          .default(RentCoverageTypes.CompanyDefault)
      : yup
          .string()
          .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.RENT_COVERAGE_REQUIRED)
          .oneOf([RentCoverageTypes.CompanyDefault, RentCoverageTypes.Customize]),
    securityDepositCoverage:
      customCoverageDisabled || useNewCoveragesOnLandlordInvite
        ? yup
            .string()
            .optional()
            .oneOf([SecurityDepositTypes.CompanyDefault, SecurityDepositTypes.CustomizeOrRemove])
            .default(SecurityDepositTypes.CompanyDefault)
        : yup
            .string()
            .required(INVITE_LEASE_DETAILS_VALIDATION_MESSAGES.SECURITY_DEPOSIT_COVERAGE_REQUIRED)
            .oneOf([SecurityDepositTypes.CompanyDefault, SecurityDepositTypes.CustomizeOrRemove]),
    selectedSmbPricing: yup.mixed<CoveragePricing>().optional().notRequired(),
    selectedCoverageRuleId: yup.string().optional(),
  });
